import React from "react";
import { fingerLabel } from "./KeyboardDiagram";
import { keyCapLabel } from "./arabicText";

function CheckIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

/** New keys a lesson introduces, drawn as small caps. */
function KeyPreview({ keys, layout }) {
  if (!keys?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2" dir="ltr">
      {keys.map((key) => {
        const spec = layout?.keys?.[key.code];
        if (!spec) return null;
        const char = key.shift ? spec.shift : spec.base;
        return (
          <span
            key={`${key.code}-${key.shift}`}
            className="inline-flex items-center justify-center rounded border font-arabic"
            style={{
              minWidth: "1.6rem",
              height: "1.6rem",
              fontSize: "0.9rem",
              background: "var(--ink)",
              borderColor: "var(--panel-line)",
              color: "var(--parchment)",
            }}
            title={`${key.shift ? "Shift + " : ""}${key.code} — ${fingerLabel(spec.finger)}`}
          >
            {keyCapLabel(char)}
          </span>
        );
      })}
    </div>
  );
}

export default function TypingLessonList({ lessons, layout, onSelect, onVocab, vocabCount }) {
  return (
    <div className="flex flex-col gap-3">
      {lessons.map((lesson) => {
        const { progress } = lesson;
        const locked = !lesson.unlocked;
        return (
          <button
            key={lesson.code}
            type="button"
            disabled={locked}
            onClick={() => onSelect(lesson.code)}
            className={`surface-glass text-left rounded-xl px-4 py-3 transition-all ${
              locked ? "cursor-not-allowed opacity-55" : "hover:translate-x-[2px]"
            }`}
            style={{
              borderColor: progress.completed ? "var(--gold)" : "var(--panel-line)",
            }}
          >
            <div className="flex items-start gap-3">
              <span
                className="flex items-center justify-center rounded-full border-2 font-cormorant font-semibold shrink-0"
                style={{
                  width: "2.1rem",
                  height: "2.1rem",
                  background: progress.completed ? "var(--gold)" : "var(--ink)",
                  borderColor: progress.completed ? "var(--gold)" : "var(--gold-dim)",
                  color: progress.completed ? "var(--ink)" : "var(--parchment-dim)",
                }}
              >
                {progress.completed ? <CheckIcon /> : lesson.order}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
                    {lesson.title}
                  </span>
                  <span className="font-arabic text-sm" dir="rtl" lang="ar" style={{ color: "var(--parchment-dim)" }}>
                    {lesson.title_arabic}
                  </span>
                  {locked && (
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--parchment-dim)" }}>
                      <LockIcon />
                      Locked
                    </span>
                  )}
                </div>

                <p className="text-sm mt-0.5" style={{ color: "var(--parchment-dim)" }}>
                  {lesson.tagline}
                </p>

                <KeyPreview keys={lesson.new_keys} layout={layout} />

                {progress.progress_percent > 0 && (
                  <div className="flex items-center gap-3 mt-2.5">
                    <div className="progress-track flex-1">
                      <div className="progress-fill" style={{ width: `${progress.progress_percent}%` }} />
                    </div>
                    <span className="text-xs tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>
                      {progress.completed_drills.length}/{progress.total_drills}
                      {progress.best_wpm > 0 && ` · ${Math.round(progress.best_wpm)} wpm`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}

      {/* Word Bank practice — outside the curriculum. Needs an account, so
          the public page passes no onVocab and the card is hidden. */}
      {onVocab && (
      <button
        type="button"
        onClick={onVocab}
        className="surface-glass text-left rounded-xl px-4 py-3 transition-all hover:translate-x-[2px]"
      >
        <div className="flex items-start gap-3">
          <span
            className="flex items-center justify-center rounded-full border-2 shrink-0"
            style={{ width: "2.1rem", height: "2.1rem", background: "var(--ink)", borderColor: "var(--gold-dim)", color: "var(--parchment-dim)" }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </span>
          <div className="min-w-0">
            <span className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
              Practise your own words
            </span>
            <p className="text-sm mt-0.5" style={{ color: "var(--parchment-dim)" }}>
              {vocabCount > 0
                ? `Type the ${vocabCount} vowelled ${vocabCount === 1 ? "word" : "words"} from your Word Bank.`
                : "Vowelled words you save to your Word Bank show up here to type."}
            </p>
          </div>
        </div>
      </button>
      )}
    </div>
  );
}
